import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Typography, Chip, Button, Stack } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatarSE } from './utils/semanaEpi';
import { API_URL } from './config';

const CARD_SX = {
  bgcolor: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  p: 2,
  borderRadius: 1.5,
};

const LABEL_SX = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'rgba(255,255,255,0.6)',
  mb: 0.5,
};

const VALUE_SX = {
  fontWeight: 700,
  color: '#fff',
  lineHeight: 1.1,
};

interface PainelHomeProps { onNavegar: (pagina: string) => void; }

export default function PainelHome({ onNavegar }: PainelHomeProps) {
  const [dados, setDados] = useState<any | null>(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/painel-home/dados`)
      .then(r => setDados(r.data))
      .catch(() => setDados(null));
  }, []);

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #0F1419 0%, #1B2A3A 50%, #0F1419 100%)', p: 3, borderRadius: 2, color: '#fff', mb: 4 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <FiberManualRecordIcon sx={{ fontSize: 10, color: '#34C759' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>
          Tovis · Tempo Real
        </Typography>
      </Box>

      {/* Null state */}
      {!dados && (
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Carregando...</Typography>
      )}

      {/* Bloco 1 — Pulso da SE */}
      {dados && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>

          {/* SE atual */}
          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <CalendarMonthIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={LABEL_SX}>SE atual</Typography>
            </Box>
            <Typography sx={{ ...VALUE_SX, fontSize: 15 }}>
              {formatarSE(dados?.pulso?.se_atual)}
            </Typography>
            {dados?.pulso?.se_intervalo && (
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
                {new Date(dados.pulso.se_intervalo.inicio + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                {' – '}
                {new Date(dados.pulso.se_intervalo.fim + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
              </Typography>
            )}
          </Box>

          {/* Área em campo */}
          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={LABEL_SX}>Área em campo</Typography>
            </Box>
            <Typography sx={{ ...VALUE_SX, fontSize: 18 }}>
              {dados?.pulso?.area_em_campo ?? '—'}
            </Typography>
          </Box>

          {/* Imóveis SE */}
          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <GroupIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={LABEL_SX}>Imóveis (SE)</Typography>
            </Box>
            <Typography variant="h4" sx={{ ...VALUE_SX, fontVariantNumeric: 'tabular-nums' }}>
              {dados?.pulso?.imoveis_trabalhados_se ?? 0}
            </Typography>
          </Box>

          {/* Dias de produção */}
          <Box sx={CARD_SX}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={LABEL_SX}>Dias produção</Typography>
            </Box>
            <Typography variant="h4" sx={{ ...VALUE_SX, fontVariantNumeric: 'tabular-nums' }}>
              {dados?.pulso?.dias_produzidos_se ?? 0}
              <Typography component="span" sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', ml: 0.5 }}>
                de 5
              </Typography>
            </Typography>
          </Box>

        </Box>
      )}

      {/* Bloco 2 — Status */}
      {dados && (
        <>
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', mt: 3, mb: 1.5 }}>
            Status
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>

            {/* Último lançamento */}
            <Box sx={CARD_SX}>
              <Typography sx={LABEL_SX}>Lançamento mais recente</Typography>
              <Typography sx={{ ...VALUE_SX, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
                {dados.equipe.ultima_data_lancada
                  ? new Date(dados.equipe.ultima_data_lancada + 'T12:00:00').toLocaleDateString('pt-BR')
                  : '—'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>Data do FAD-01</Typography>
            </Box>

            {/* Pendências abertas da área em campo */}
            <Box sx={CARD_SX}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <WarningIcon sx={{ fontSize: 14, color: '#FFC107' }} />
                <Typography sx={LABEL_SX}>Pendências Abertas</Typography>
              </Box>
              <Typography sx={{ ...VALUE_SX, fontSize: 28, fontVariantNumeric: 'tabular-nums', color: '#FFC107' }}>
                {dados?.atencoes?.pendencias_abertas_area ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                {dados?.pulso?.area_em_campo ?? '—'}
              </Typography>
            </Box>

            {/* Recuperados da área em campo */}
            <Box sx={CARD_SX}>
              <Typography sx={LABEL_SX}>Recuperados</Typography>
              <Typography sx={{ ...VALUE_SX, fontSize: 28, fontVariantNumeric: 'tabular-nums', color: '#4ADE80' }}>
                {dados?.atencoes?.recuperados_area ?? 0}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
                {dados?.pulso?.area_em_campo ?? '—'}
              </Typography>
            </Box>

          </Box>
        </>
      )}

      {/* Bloco 4 — Resumo da Semana */}
      {dados && (
        <>
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 1, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', mt: 3, mb: 1.5 }}>
            {`Resumo do Ciclo · ${dados?.pulso?.area_em_campo ?? ''}`}
          </Typography>

          <Stack direction="row" sx={{ gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`Imóveis Informados: ${dados?.resumo_ciclo_area?.imoveis_informados ?? 0}`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 13, bgcolor: 'rgba(96,165,250,0.15)', color: '#60A5FA', borderColor: 'rgba(96,165,250,0.3)' }} />
            <Chip label={`Quart. Concluídos: ${dados?.resumo_ciclo_area?.quarteiroes_concluidos ?? 0}`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 13, bgcolor: 'rgba(96,165,250,0.15)', color: '#60A5FA', borderColor: 'rgba(96,165,250,0.3)' }} />
            <Chip label={`Fechados: ${dados?.resumo_ciclo_area?.fechados ?? 0}`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 13, bgcolor: 'rgba(255,193,7,0.15)', color: '#FFC107', borderColor: 'rgba(255,193,7,0.3)' }} />
            <Chip label={`Recuperados: ${dados?.resumo_ciclo_area?.recuperados ?? 0}`}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: 13, bgcolor: 'rgba(74,222,128,0.15)', color: '#4ADE80', borderColor: 'rgba(74,222,128,0.3)' }} />
          </Stack>

          <Stack direction="row" sx={{ gap: 2, mt: 2, flexWrap: 'wrap' }}>
            {/* Upload */}
            <Button variant="outlined" onClick={() => onNavegar('upload')}
              sx={{ textTransform: 'none', fontSize: 13, borderColor: 'rgba(255,255,255,0.2)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)', px: 2.5, py: 1.5, borderRadius: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              📤 Importar planilha
              <Typography component="span" sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', mt: 0.25, textTransform: 'none' }}>
                Último upload: {dados?.atalhos?.ultimo_upload_data
                  ? new Date(dados.atalhos.ultimo_upload_data + 'T12:00:00').toLocaleDateString('pt-BR')
                  : '—'}
              </Typography>
            </Button>

            {/* Pendências — só aparece se tem_pendencias */}
            {dados?.atalhos?.tem_pendencias && (
              <Button variant="outlined" onClick={() => onNavegar('pendencias')}
                sx={{ textTransform: 'none', fontSize: 13, borderColor: 'rgba(255,193,7,0.4)', color: '#FFC107', bgcolor: 'rgba(255,193,7,0.07)', px: 2.5, py: 1.5, borderRadius: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', '&:hover': { bgcolor: 'rgba(255,193,7,0.15)' } }}>
                ⚠️ Ver Pendências
                <Typography component="span" sx={{ fontSize: 10, color: 'rgba(255,193,7,0.7)', mt: 0.25, textTransform: 'none' }}>
                  {dados?.atencoes?.pendencias_abertas_area ?? 0} em aberto · {dados?.pulso?.area_em_campo ?? ''}
                </Typography>
              </Button>
            )}

            {/* Resumo Geral */}
            <Button variant="outlined" onClick={() => onNavegar('resumo_geral')}
              sx={{ textTransform: 'none', fontSize: 13, borderColor: 'rgba(255,255,255,0.2)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)', px: 2.5, py: 1.5, borderRadius: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              📊 Resumo Geral
            </Button>
          </Stack>
        </>
      )}

    </Box>
  );
}
